import React from 'react';
import { Target } from '@phosphor-icons/react';

// Analytics API row shape from learn_my_skill_development metric
interface SkillRow {
    categoryName: string;
    avgProgress: number;
    courseCount: number;
}

interface SkillDevelopmentWidgetProps {
    data?: SkillRow[] | null;
    loading?: boolean;
    error?: string;
}

const SKILL_COLORS = [
    { bar: 'bg-primary', text: 'text-primary', bg: 'bg-primary/10' },
    { bar: 'bg-emerald-500', text: 'text-emerald-600', bg: 'bg-emerald-50' },
    { bar: 'bg-amber-500', text: 'text-amber-600', bg: 'bg-amber-50' },
    { bar: 'bg-blue-500', text: 'text-blue-600', bg: 'bg-blue-50' },
    { bar: 'bg-purple-500', text: 'text-purple-600', bg: 'bg-purple-50' },
    { bar: 'bg-rose-500', text: 'text-rose-600', bg: 'bg-rose-50' },
];

export const SkillDevelopmentWidget: React.FC<SkillDevelopmentWidgetProps> = ({ data, loading, error }) => {
    const skills = Array.isArray(data) ? data : [];

    return (
        <div className="flex flex-col h-full">
            <div className="px-4 pt-4 pb-4 flex items-center justify-between">
                <span className="text-xs text-gray-400">By category</span>
            </div>
            <div className="flex-1 overflow-auto px-4 pb-4">
                {loading ? (
                    <div className="flex items-center justify-center h-full">
                        <div className="w-6 h-6 border-3 border-gray-200 border-t-primary rounded-full animate-spin" />
                    </div>
                ) : error ? (
                    <div className="flex items-center justify-center h-full text-red-500 text-sm">{error}</div>
                ) : skills.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-full text-gray-400 gap-2">
                        <Target className="w-8 h-8" />
                        <span className="text-sm">No skill data available</span>
                    </div>
                ) : (
                    <div className="space-y-4">
                        {skills.map((skill, idx) => {
                            const color = SKILL_COLORS[idx % SKILL_COLORS.length];
                            const progress = Number(skill.avgProgress) || 0;
                            const courseCount = Number(skill.courseCount) || 0;
                            return (
                                <div key={skill.categoryName}>
                                    <div className="flex items-center justify-between mb-1.5">
                                        <span className="text-sm text-gray-800">{skill.categoryName}</span>
                                        <div className="flex items-center gap-2">
                                            <span className="text-[10px] text-gray-400">{courseCount} courses</span>
                                            <span className={`text-xs font-medium ${color.text}`}>{progress}%</span>
                                        </div>
                                    </div>
                                    <div className="w-full h-2.5 bg-gray-100 rounded-full overflow-hidden">
                                        <div
                                            className={`h-full ${color.bar} rounded-full transition-all duration-500`}
                                            style={{ width: `${progress}%` }}
                                        />
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    );
};
